import {
  CaretLeftIcon,
  CaretRightIcon,
  MapPinAreaIcon,
  MapPinIcon,
} from '@phosphor-icons/react'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import IconGradientWrapper from '@/src/components/core/icons/IconGradientWrapper'
import { formatGerman } from '@/src/components/core/lib/date'
import {
  Course,
  COURSE_TYPE_SHORT_NAMES,
  courseStartDate,
  formatAddress,
  getCourseDays,
  getSeatText,
  getTagText,
} from '@/src/components/flch/courseLocation/lib'

type MonthGroup = {
  key: string
  name: string
  courses: Course[]
}

type LocationGroup = {
  id: number
  name: string
  distanceKm: number
  locationSlug: string
  courses: Course[]
}

export function getLocationLayout(
  courses: Course[],
  locationId: number | null
) {
  const localCourses = courses.filter(
    (course) => course.locationId === locationId
  )
  const mode = localCourses.length > 0 ? 'offers' : 'nearby'
  return { localCourses, mode } as const
}

function groupByMonth(courses: Course[]): MonthGroup[] {
  const groups = new Map<string, MonthGroup>()
  for (const course of courses) {
    const start = courseStartDate(course.course)
    const key = formatGerman(start, 'yyyy-MM')
    const existing = groups.get(key)
    if (existing) {
      existing.courses.push(course)
    } else {
      groups.set(key, {
        key,
        name: formatGerman(start, 'LLLL'),
        courses: [course],
      })
    }
  }
  const sorted = [...groups.values()].sort((a, b) => a.key.localeCompare(b.key))
  for (const group of sorted) {
    group.courses.sort(
      (a, b) =>
        courseStartDate(a.course).getTime() -
        courseStartDate(b.course).getTime()
    )
  }
  return sorted
}

function groupByLocation(courses: Course[]): LocationGroup[] {
  const groups = new Map<number, LocationGroup>()
  for (const course of courses) {
    const existing = groups.get(course.locationId)
    if (existing) {
      existing.courses.push(course)
    } else {
      groups.set(course.locationId, {
        id: course.locationId,
        name: course.locationName,
        distanceKm: course.distanceKm,
        locationSlug: course.locationSlug,
        courses: [course],
      })
    }
  }
  const sorted = [...groups.values()].sort(
    (a, b) => a.distanceKm - b.distanceKm
  )
  for (const group of sorted) {
    group.courses.sort(
      (a, b) =>
        courseStartDate(a.course).getTime() -
        courseStartDate(b.course).getTime()
    )
  }
  return sorted
}

const List = ({
  filteredCourses,
  locationId,
  courses,
  swissPlaceName,
  courseTypeGroup,
}: {
  filteredCourses: Course[]
} & FahrlehrerCh.CoursesPage) => {
  const { localCourses, mode } = getLocationLayout(courses, locationId)

  if (mode === 'nearby') {
    const nearbyGroups = groupByLocation(
      filteredCourses.filter((course) => course.locationId !== locationId)
    )
    const courseLabel = COURSE_TYPE_SHORT_NAMES[courseTypeGroup] ?? 'passenden'

    return (
      <div className="mt-6">
        <NoCoursesHint placeName={swissPlaceName} courseLabel={courseLabel} />
        {nearbyGroups.length > 0 && (
          <div className="mt-12 space-y-18">
            {nearbyGroups.map((group) => (
              <NearbyLocationGroup
                key={group.id}
                group={group}
                courseTypeGroup={courseTypeGroup}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  const monthGroups = groupByMonth(
    filteredCourses.filter((course) => course.locationId === locationId)
  )
  const localCourse = localCourses[0]

  return (
    <div className="mt-6">
      <div className="space-y-18">
        {monthGroups.map((group) => (
          <div key={group.key}>
            <h3 className="mb-6 text-lg font-bold">{group.name}</h3>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              {group.courses.map((course) => (
                <CourseCard key={course.course.slug} {...course} />
              ))}
            </div>
          </div>
        ))}
      </div>
      {localCourse && (
        <BlinkCourseHint
          courseTypeGroup={courseTypeGroup}
          locationName={localCourse.locationName}
          locationSlug={localCourse.locationSlug}
        />
      )}
    </div>
  )
}

const BlinkCourseHint = ({
  courseTypeGroup,
  locationName,
  locationSlug,
}: {
  courseTypeGroup: string
  locationName: string
  locationSlug: string
}) => {
  const location = locationSlug.replace(/^\/[^-]*-/, '')

  switch (courseTypeGroup) {
    case 'firstaid':
      return (
        <p className="mt-6 text-sm text-gray-800">
          Die Nothelferkurse in {locationName} werden von führenden Anbietern
          wie der{' '}
          <Link
            href={`https://blinkdrive.ch/nothelferkurs-${location}/`}
            target="_blank"
            className="font-bold text-blue-700"
          >
            BLINK Fahrschule
          </Link>{' '}
          und{' '}
          <Link
            href={`https://nohe.ch/nothelferkurs-${location}/`}
            target="_blank"
            className="font-bold text-blue-700"
          >
            nohe
          </Link>{' '}
          durchgeführt.
        </p>
      )
    case 'vku':
      return (
        <p className="mt-6 text-sm text-gray-800">
          Der{' '}
          <Link
            href={`https://blinkdrive.ch/vku-${location}`}
            target="_blank"
            className="font-bold text-blue-700"
          >
            Verkehrskundeunterricht in {locationName}
          </Link>{' '}
          wird durch die BLINK Fahrschule durchgeführt.
        </p>
      )
    case 'pgs':
      return (
        <p className="mt-6 text-sm text-gray-800">
          Die{' '}
          <Link
            href={`https://blinkdrive.ch/motorradgrundkurs-${location}`}
            target="_blank"
            className="font-bold text-blue-700"
          >
            Motorradgrundkurse in {locationName}
          </Link>{' '}
          werden durch die BLINK Fahrschule durchgeführt.
        </p>
      )
    case 'wab':
      return (
        <p className="mt-6 text-sm text-gray-800">
          Die{' '}
          <Link
            href={`https://blinkdrive.ch/wab-kurs-${location}`}
            target="_blank"
            className="font-bold text-blue-700"
          >
            WAB Kurse in {locationName}
          </Link>{' '}
          werden durch die BLINK Fahrschule vermittelt.
        </p>
      )
    default:
      return null
  }
}

const CourseCard = ({ course }: Course) => {
  const router = useRouter()
  const tagText = getTagText(course)
  const seatText = getSeatText(course)
  const days = getCourseDays(course)

  return (
    <div className="flex h-full flex-col justify-between rounded-xl border border-gray-150 bg-gray-50 px-6 pt-5 pb-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={
              course.type === 'eNothelfer'
                ? 'rounded-sm bg-pink-600 px-2 py-1 text-xs font-bold text-white'
                : 'rounded-sm bg-blue-150 px-2 py-1 text-xs font-bold'
            }
          >
            {tagText}
          </span>
          {seatText && (
            <span className="text-xs font-bold text-yellow-700">
              {seatText}
            </span>
          )}
          {course.promotionName && (
            <span className="text-xs font-bold text-green-700">
              {course.promotionName}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-4">
          {days.map((day) => (
            <div key={day.isoDay} className="flex flex-col gap-1">
              <p className="text-lg font-black">
                <time dateTime={day.isoDay}>{day.dateText}</time>
              </p>
              <p className="text-gray-800">{day.timeText}</p>
            </div>
          ))}
          <div className="flex items-center gap-2 text-gray-800">
            <MapPinIcon
              size={20}
              weight="fill"
              className="shrink-0 text-gray-600"
            />
            <span>{formatAddress(course.address)}</span>
          </div>
        </div>
      </div>
      <div className="mt-8 flex items-end justify-between gap-4">
        <div>
          <div className="flex items-baseline gap-2">
            <p className="text-lg font-black">{course.price / 100} CHF</p>
            {course.communicatedOriginalPrice && (
              <p className="text-sm text-gray-800 line-through">
                {course.communicatedOriginalPrice / 100} CHF
              </p>
            )}
          </div>
          {course.priceDescription && (
            <p className="text-xs text-gray-600">{course.priceDescription}</p>
          )}
        </div>
        {course.type === 'WAB' ? (
          course.registrationUrl && (
            <Button
              as={Link}
              href={course.registrationUrl}
              target="_blank"
              rel="noopener noreferrer"
              version="gray-outlined"
              size="small"
              className="shrink-0"
            >
              zur Kursbuchung
            </Button>
          )
        ) : (
          <Button
            as={Link}
            href={{ query: { ...router.query, course: course.slug } }}
            shallow
            version="gray-outlined"
            size="small"
            className="shrink-0"
          >
            Auswählen
          </Button>
        )}
      </div>
    </div>
  )
}

const NoCoursesHint = ({
  placeName,
  courseLabel,
}: {
  placeName: string
  courseLabel: string
}) => (
  <div className="relative overflow-hidden rounded-2xl bg-gray-50 p-6">
    <div className="flex flex-col gap-3 sm:max-w-150">
      <p className="text-lg font-black">
        In {placeName} gibt es derzeit keine {courseLabel} Kurse die
        stattfinden.
      </p>
      <p>
        Damit du trotzdem schnell fündig wirst, hier einige Standorte aus der
        Umgebung mit {courseLabel} Kursen.
      </p>
    </div>
    <span className="pointer-events-none absolute top-3 right-8 hidden xl:block">
      <IconGradientWrapper from="#1a34d0" to="#1628a4">
        <MapPinAreaIcon weight="bold" className="size-38" />
      </IconGradientWrapper>
    </span>
  </div>
)

const LocationGroupHeader = ({
  name,
  distanceKm,
}: {
  name: string
  distanceKm: number
}) => (
  <div className="flex items-center gap-2">
    <MapPinAreaIcon
      weight="bold"
      size={24}
      className="shrink-0 text-gray-800"
    />
    <span className="text-lg font-bold">
      {name}, {Math.round(distanceKm)} km
    </span>
  </div>
)

const MoreCoursesCard = ({
  moreCoursesCount,
  location,
  locationSlug,
}: {
  moreCoursesCount: number
  location: string
  locationSlug: string
}) => {
  const moreText =
    moreCoursesCount === 1
      ? `1 weiterer Kurs in ${location}`
      : `${moreCoursesCount} weitere Kurse in ${location}`
  return (
    <Link
      href={locationSlug}
      className="flex h-full flex-col items-center justify-center rounded-xl bg-gray-50 px-6 py-8 text-center"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="194"
        height="169"
        fill="none"
      >
        <rect
          width="108"
          height="108"
          x="86.289"
          y="13.8"
          fill="#e7ebf8"
          rx="18"
          transform="rotate(6 86.29 13.8)"
        />
        <rect
          width="108"
          height="108"
          y="24.712"
          fill="#e7ebf8"
          rx="18"
          transform="rotate(-6 0 24.712)"
        />
        <g filter="url(#a)">
          <g clip-path="url(#b)">
            <path
              fill="url(#c)"
              d="M0 0h120v30H0z"
              transform="translate(37 8.5)"
            />
            <path fill="#fff" d="M37 38.5h120v90H37z" />
            <path
              fill="#0d0b3f"
              d="M88.776 73.59v34.91h-9.478V82.318h-.204l-7.637 4.568v-8.045l8.591-5.25zm7.167 34.91v-6.818l13.023-10.773a30 30 0 0 0 2.216-2.011q.937-.955 1.432-1.96.511-1.006.511-2.234 0-1.346-.58-2.3a3.75 3.75 0 0 0-1.568-1.466q-1.005-.53-2.318-.529-1.313 0-2.318.528a3.67 3.67 0 0 0-1.534 1.552q-.546 1.022-.546 2.488h-9q0-3.681 1.654-6.34 1.653-2.66 4.67-4.091t7.074-1.432q4.194 0 7.261 1.346 3.086 1.33 4.756 3.75 1.688 2.421 1.688 5.676 0 2.012-.836 4.006-.834 1.978-3 4.38-2.165 2.404-6.153 5.728l-3.273 2.727v.205h13.637v7.568z"
            />
            <path
              fill="#9097bc"
              d="M73.155 62.5v-8.727h5.233v.937h-4.176v2.95h3.784v.937h-3.784V62.5zm7.283 0v-8.727h2.949q1.023 0 1.679.35.656.344.972.95.315.605.315 1.376t-.315 1.368-.968.937q-.651.337-1.666.337h-2.386v-.955h2.352q.698 0 1.125-.204.43-.205.622-.58.195-.38.196-.903 0-.525-.196-.916a1.34 1.34 0 0 0-.626-.605q-.43-.218-1.138-.218h-1.858v7.79zm4.108-3.92 2.148 3.92h-1.227l-2.114-3.92zm3.808 3.92v-8.727h5.267v.937h-4.21v2.95h3.937v.937h-3.937v2.965h4.278v.938zm8.469-8.727V62.5h-1.057v-8.727zm1.881.937v-.937h6.546v.937h-2.745v7.79h-1.056v-7.79zm7.755 7.79h-1.108l3.205-8.727h1.091l3.204 8.727h-1.108l-2.608-7.347h-.068zm.409-3.41h4.466v.938h-4.466zm13.138-2.59a3 3 0 0 0-.37-.771 2.4 2.4 0 0 0-.542-.588 2.3 2.3 0 0 0-.707-.371 2.8 2.8 0 0 0-.869-.128q-.775 0-1.411.4-.635.402-1.01 1.181-.375.78-.375 1.913t.38 1.914q.379.78 1.027 1.18t1.457.4q.75 0 1.321-.319a2.27 2.27 0 0 0 .895-.912q.324-.593.324-1.393l.324.068h-2.625v-.938h3.323v.938q0 1.078-.46 1.875a3.2 3.2 0 0 1-1.261 1.236q-.801.435-1.841.434-1.16 0-2.037-.545-.873-.545-1.364-1.551-.485-1.005-.485-2.387 0-1.035.277-1.862a4.1 4.1 0 0 1 .792-1.415q.512-.583 1.21-.895a3.7 3.7 0 0 1 1.539-.31q.69 0 1.287.208a3.45 3.45 0 0 1 1.858 1.483q.315.52.434 1.155z"
            />
          </g>
          <rect
            width="115"
            height="119"
            x="39.5"
            y="9"
            stroke="#c7cce5"
            rx="17.5"
          />
        </g>
        <defs>
          <linearGradient
            id="c"
            x1="60"
            x2="60"
            y1="0"
            y2="30"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#1a34d0" />
            <stop offset="1" stop-color="#1628a4" />
          </linearGradient>
          <clipPath id="b">
            <rect width="116" height="120" x="39" y="8.5" fill="#fff" rx="18" />
          </clipPath>
          <filter
            id="a"
            width="156"
            height="160"
            x="19"
            y="8.5"
            color-interpolation-filters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              result="hardAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            />
            <feMorphology
              in="SourceAlpha"
              radius="4"
              result="effect1_dropShadow_2770_4125"
            />
            <feOffset dy="20" />
            <feGaussianBlur stdDeviation="12" />
            <feColorMatrix values="0 0 0 0 0.0470588 0 0 0 0 0.0509804 0 0 0 0 0.176471 0 0 0 0.08 0" />
            <feBlend
              in2="BackgroundImageFix"
              result="effect1_dropShadow_2770_4125"
            />
            <feColorMatrix
              in="SourceAlpha"
              result="hardAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            />
            <feMorphology
              in="SourceAlpha"
              radius="4"
              result="effect2_dropShadow_2770_4125"
            />
            <feOffset dy="8" />
            <feGaussianBlur stdDeviation="4" />
            <feColorMatrix values="0 0 0 0 0.0470588 0 0 0 0 0.0509804 0 0 0 0 0.176471 0 0 0 0.03 0" />
            <feBlend
              in2="effect1_dropShadow_2770_4125"
              result="effect2_dropShadow_2770_4125"
            />
            <feColorMatrix
              in="SourceAlpha"
              result="hardAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            />
            <feMorphology
              in="SourceAlpha"
              radius="1.5"
              result="effect3_dropShadow_2770_4125"
            />
            <feOffset dy="3" />
            <feGaussianBlur stdDeviation="1.5" />
            <feColorMatrix values="0 0 0 0 0.0470588 0 0 0 0 0.0509804 0 0 0 0 0.176471 0 0 0 0.04 0" />
            <feBlend
              in2="effect2_dropShadow_2770_4125"
              result="effect3_dropShadow_2770_4125"
            />
            <feBlend
              in="SourceGraphic"
              in2="effect3_dropShadow_2770_4125"
              result="shape"
            />
          </filter>
        </defs>
      </svg>
      <span className="text-lg font-bold">{moreText}</span>
      <span className="mt-3 flex items-center font-bold text-blue-700">
        Alle Kurse anzeigen{' '}
        <CaretRightIcon size="20" weight="bold" className="ml-1" />
      </span>
    </Link>
  )
}

const NearbyLocationGroup = ({
  group,
  courseTypeGroup,
}: {
  group: LocationGroup
  courseTypeGroup: string
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    breakpoints: {
      '(min-width: 768px)': { skipSnaps: true },
    },
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('select', onSelect).on('reInit', onSelect)
  }, [emblaApi, onSelect])

  const visibleCourses = group.courses.slice(0, 3)
  const hasMore = group.courses.length > visibleCourses.length

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <LocationGroupHeader name={group.name} distanceKm={group.distanceKm} />
        <Link
          href={group.locationSlug}
          className="mt-3 hidden items-center font-bold text-blue-700 lg:flex"
        >
          Alle Kurse anzeigen
          <CaretRightIcon size="20" weight="bold" className="ml-1 shrink-0" />
        </Link>
      </div>
      <div
        ref={emblaRef}
        className="-mx-5 mt-6 overflow-hidden md:-mr-[calc(max(0px,(100vw-var(--max-width-container))/2-8px)+24px)] md:-ml-16 xl:-ml-34"
      >
        <div className="flex items-stretch select-none">
          {visibleCourses.map((course) => (
            <div
              key={course.course.slug}
              className="min-w-0 flex-[0_0_88%] pl-4 first:ml-5 first:pl-0 last:mr-5 md:flex-[0_0_410px] md:first:ml-16 md:last:mr-6 xl:first:ml-34"
            >
              <CourseCard {...course} />
            </div>
          ))}
          {hasMore && (
            <div className="mr-5 min-w-0 flex-[0_0_88%] pl-4 first:pl-0 md:mr-6 md:flex-[0_0_410px]">
              <MoreCoursesCard
                moreCoursesCount={group.courses.length - visibleCourses.length}
                location={group.name}
                locationSlug={group.locationSlug}
              />
            </div>
          )}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-between lg:justify-end">
        <Link
          href={group.locationSlug}
          className="mt-3 flex items-center font-bold text-blue-700 lg:hidden"
        >
          Alle Kurse anzeigen
          <CaretRightIcon size="20" weight="bold" className="ml-1 shrink-0" />
        </Link>
        <div className="hidden gap-3 lg:flex">
          <Button
            icon={<CaretLeftIcon weight="bold" />}
            size="icon-small"
            version="gray-outlined"
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canScrollPrev}
            aria-label="Vorherige Kurse"
          />
          <Button
            icon={<CaretRightIcon weight="bold" />}
            size="icon-small"
            version="gray-outlined"
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canScrollNext}
            aria-label="Nächste Kurse"
          />
        </div>
      </div>
      <BlinkCourseHint
        courseTypeGroup={courseTypeGroup}
        locationName={group.name}
        locationSlug={group.locationSlug}
      />
    </div>
  )
}

export default List
